import React, {useCallback, useMemo, useState} from 'react';
import SMCButton from '../../../../../../components/Simple/SMCButton';
import {useTranslation} from 'react-i18next';
import {useAccessPolicyUtils} from './util';
import SMCTable from '../../../../../../components/core/SMCTable/SMCTable';
import {useBoolean} from '../../../../../../utils/hooks';
import AccessPolicyLegacyIncomingEditorModal from '../../../../../PopupPages/AccessPolicy/AccessPolicyLegacyIncomingEditorModal';
import AccessPolicyLegacyOutgoingEditorModal from '../../../../../PopupPages/AccessPolicy/AccessPolicyLegacyOutgoingEditorModal';
import AccessPolicyNextGenIncomingEditorModal from '../../../../../PopupPages/AccessPolicy/AccessPolicyNextGenIncomingEditorModal';
import AccessPolicyNextGenOutgoingEditorModal from '../../../../../PopupPages/AccessPolicy/AccessPolicyNextGenOutgoingEditorModal';
import {CpRuleSchema} from '../../../../../../api/DBSchema/CPRule';
import {swapItemsInList} from '../../../../../../utils/utils';
import redXLogo from '../../../../../../assets/images/red_x.png';
import greenCheckLogo from '../../../../../../assets/images/green_check.png';
import SMCIcon from '../../../../../../components/Simple/SMCIcon';
import RowLayout from '../../../../../../components/Layouts/RowLayout';
import ButtonsContainerStyles from "../../../../ButtonsContainerStyles";

/**
 * @param {Object} props
 * @param {boolean} [props.disabled]
 * @param {string} props.baseObjectName
 * @param {string} props.objectType
 * @param {('incoming'|'outgoing')} props.incomingOrOutgoing
 * @param {('legacy'|'nextGen')} props.legacyOrNextGen
 *  @param {Object} [props.appiData]
 */
function AccessPolicyTableLegacy({
    rules,
    disabled,
    setRules,
    baseObjectName,
    objectType,
    incomingOrOutgoing,
    legacyOrNextGen,
    appiData,
    uoTableData,
    zonesData
}) {
    const { t } = useTranslation();
    const { getServiceOrApplicationString, getNetworkObjectString, getActionIcon, getActionString, getLogIcon, getLogString, getServiceOrApplicationTitle, getNetworkObjectTitle } = useAccessPolicyUtils(appiData, uoTableData, zonesData);
    const [isPolicyEditorOpen, openPolicyEditor, closePolicyEditor] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);

    // Editor objects
    const [editorObject, setEditorObject] = useState(null);
    const labelClasses = ButtonsContainerStyles();

    const applicationOrserviceHeader = useMemo(() => {
        if (legacyOrNextGen === 'nextGen' && incomingOrOutgoing === 'outgoing') {
            return "Application / Service";
        }
        else {
            return "Service";
        }
    }, [legacyOrNextGen, incomingOrOutgoing]);

    const rulesTableColumns = useMemo(() => ([
        { id: 'count',                                           header: '#',           width: 32,  align: 'center',  Cell: ({index}) => (index + 1)                                                                                                                                        },
        { id: 'edit',                                            header: '',            width: 22,                    Cell: ({index, object}) => ( <SMCIcon icon="edit" align="absmiddle" onClick={() => { setEditorObject(object); openPolicyEditor(); }} />) },
        { id: 'source',      accessor: CpRuleSchema.source,      header: 'Source',      width: 225,                   Cell: ({value}) => ( <span title={getNetworkObjectTitle(value)}>{getNetworkObjectString(value)}</span> )                                                                                                   },
        { id: 'destination', accessor: CpRuleSchema.destination, header: 'Destination', width: 225,                   Cell: ({value}) => ( <span title={getNetworkObjectTitle(value)}>{getNetworkObjectString(value)}</span> )                                                                                                   },
        { id: 'serviceOrApplication',          header:  applicationOrserviceHeader,     width: 150,                   Cell: ({object}) => ( <span title={getServiceOrApplicationTitle(object)}>{getServiceOrApplicationString(object)}</span> )                                                                                          },
        { id: 'action',      accessor: CpRuleSchema.action,      header: 'Action',      width: 140,                   Cell: ({value}) => ( <RowLayout><img align="absmiddle" alt="" src={getActionIcon(value)} />{getActionString(value)}</RowLayout> )                                     },
        { id: 'log',         accessor: CpRuleSchema.log,         header: 'Log',         width: 120,                   Cell: ({value}) => ( <RowLayout><img align="absmiddle" alt="" src={getLogIcon(value)} />{getLogString(value)}</RowLayout> )                                           },
        { id: 'description', accessor: CpRuleSchema.description, header: 'Description', width: 185                                                                                                                                                                                          },
        { id: 'enabled',     accessor: CpRuleSchema.enabled,     header: 'Enabled',     width: 75,  align: 'center',  Cell: ({value}) => ( <img alt="" src={value ? greenCheckLogo : redXLogo} /> )                                                                                         }
    ]),[applicationOrserviceHeader, getActionIcon, getActionString, getLogIcon, getLogString, getNetworkObjectString, getServiceOrApplicationString, getNetworkObjectTitle, getServiceOrApplicationTitle, openPolicyEditor]);
    
    const _rules = rules.map((rule, i) => ({...rule, index: i}));

    const editPolicyCallback = useCallback((newRule) => {
        const rulesCopy = [...rules];
        rulesCopy[editorObject.index] = newRule;
        setRules(rulesCopy);
        setEditorObject(null);
        closePolicyEditor();
    }, [editorObject, closePolicyEditor, rules, setRules]);

    const newPolicyCallback = useCallback((newRule) => {
        const rulesCopy = rules ? [...rules] : [];
        rulesCopy.push(newRule);
        setRules(rulesCopy);
        closePolicyEditor();
    }, [rules, closePolicyEditor, setRules]);

    const onClosePolicyEditor = useCallback(() => {
        setEditorObject(null);
        closePolicyEditor();
    }, [closePolicyEditor]);

    const rulesDelete = useCallback(() => {
        setRules(rules.filter((elem, i) => {
            const normalizedIndex = i + 1;
            return selectedObjects.indexOf(normalizedIndex) === -1;
             }));
        setSelectedObjects([]);
    }, [selectedObjects, rules, setRules]);
    

    const rulesOnDragEnd = useCallback(result => {
        // dropped outside the list
        if (!result.destination) {
          return;
        }
    
        setRules(swapItemsInList(rules, result.source.index, result.destination.index));
        setSelectedObjects([]);
    }, [setRules, rules]);

    const EditorModal = useMemo(() => {
        if (legacyOrNextGen === 'legacy' && incomingOrOutgoing === 'incoming') {
                return AccessPolicyLegacyIncomingEditorModal;
        } else if (legacyOrNextGen === 'legacy' && incomingOrOutgoing === 'outgoing') {
            return AccessPolicyLegacyOutgoingEditorModal;
        } else if (legacyOrNextGen === 'nextGen' && incomingOrOutgoing === 'incoming') {
            return AccessPolicyNextGenIncomingEditorModal;
        } else {
            return AccessPolicyNextGenOutgoingEditorModal;
        }
    }, [legacyOrNextGen, incomingOrOutgoing]);

    return (
        <>
        <div style={{ height: 22 }}>
            <SMCButton labelClassName={labelClasses.labelColor} variant="ghost" icon="new" disabled={disabled} onClick={openPolicyEditor}>{t('SMC_GEN_NEW')}</SMCButton>
            <SMCButton labelClassName={labelClasses.labelColor} variant="ghost" icon="delete" disabled={disabled} onClick={rulesDelete}>{t('SMC_GEN_DELETE')}</SMCButton>
        </div>
 
        <SMCTable
            columns={rulesTableColumns}
            content={_rules}
            selectedObjects={selectedObjects}
            setSelectedObjects={setSelectedObjects}
            hasCheckAll={true}
            isDraggable={!disabled}
            onDragEnd={rulesOnDragEnd}
            rowIdSelector={(obj, index) => index + 1}
            width={1280}
        />

        <EditorModal
            isOpen={isPolicyEditorOpen}
            onCancel={onClosePolicyEditor}
            onSuccess={editorObject ? editPolicyCallback : newPolicyCallback}
            defaultObject={editorObject}
            baseObjectName={baseObjectName}
            objectType={objectType}
            appiData={appiData}
            uoTableData={uoTableData}
            zonesData={zonesData}
        />
        </>
    );
}

export default AccessPolicyTableLegacy;